var _dec, _dec2, _class, _ToggleDetails;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import { Button } from '@instructure/ui-buttons';
import { IconArrowOpenEndSolid, IconArrowOpenDownSolid } from '@instructure/ui-icons';
import { Expandable } from '@instructure/ui-expandable';
import { omitProps, pickProps } from '@instructure/ui-react-utils';
import { isActiveElement } from '@instructure/ui-dom-utils';
import { testable } from '@instructure/ui-testable';
import { withStyle } from '@instructure/emotion';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { allowedProps, propTypes } from './props';
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
---
category: components
---
**/
let ToggleDetails = (_dec = withStyle(generateStyle, generateComponentTheme), _dec2 = testable(), _dec(_class = _dec2(_class = (_ToggleDetails = class ToggleDetails extends Component {
  constructor(...args) {
    super(...args);
    this.ref = null;
    this._button = null;
    this.getButtonRef = el => this._button = el;
    this.handleToggle = (event, expanded) => {
      if (typeof this.props.onToggle === 'function') {
        this.props.onToggle(event, expanded);
      }
    };
  }
  get focused() {
    return isActiveElement(this._button);
  }
  focus() {
    var _this$_button;
    (_this$_button = this._button) === null || _this$_button === void 0 ? void 0 : _this$_button.focus();
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props);
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props2;
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2);
  }
  renderSummary(expanded) {
    var _this$props$styles, _this$props$styles2;
    const _this$props3 = this.props,
      summary = _this$props3.summary,
      iconPosition = _this$props3.iconPosition;
    return _jsxs("span", {
      css: (_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.summary,
      children: [iconPosition === 'start' && this.renderIcon(expanded), _jsx("span", {
        css: (_this$props$styles2 = this.props.styles) === null || _this$props$styles2 === void 0 ? void 0 : _this$props$styles2.summaryText,
        children: summary
      }), iconPosition === 'end' && this.renderIcon(expanded)]
    });
  }
  renderToggle(toggleProps, expanded) {
    const variant = this.props.variant;
    // Do not put aria-controls and aria-expanded into the toggle if there
    // is nothing to open
    const tProps = this.props.children ? toggleProps : {
      onClick: toggleProps.onClick
    };
    const props = {
      ...omitProps(this.props, ToggleDetails.allowedProps),
      ...tProps,
      children: this.renderSummary(expanded)
    };
    const summary = this.renderSummary(expanded);
    if (variant === 'filled') {
      return _jsx(Button, {
        ...props,
        display: "block",
        textAlign: "start",
        elementRef: this.getButtonRef,
        children: summary
      });
    } else if (props.href) {
      var _this$props$styles3;
      return _jsx("a", {
        ...props,
        css: (_this$props$styles3 = this.props.styles) === null || _this$props$styles3 === void 0 ? void 0 : _this$props$styles3.toggle,
        ref: this.getButtonRef,
        children: summary
      });
    } else {
      var _this$props$styles4;
      return _jsx("button", {
        ...props,
        type: "button",
        css: (_this$props$styles4 = this.props.styles) === null || _this$props$styles4 === void 0 ? void 0 : _this$props$styles4.toggle,
        ref: this.getButtonRef,
        children: summary
      });
    }
  }
  renderIcon(expanded) {
    var _this$props$styles5;
    const Icon = expanded ? this.props.iconExpanded : this.props.icon;
    return this.props.children && Icon ? _jsx("span", {
      css: (_this$props$styles5 = this.props.styles) === null || _this$props$styles5 === void 0 ? void 0 : _this$props$styles5.icon,
      children: _jsx(Icon, {})
    }) : null;
  }
  renderDetails(expanded, detailsProps) {
    var _this$props$styles6;
    if (!this.props.children) return null;
    const expandedStyles = expanded ? {
      display: 'block'
    } : {
      display: 'none'
    };
    return _jsx("div", {
      ...detailsProps,
      css: [(_this$props$styles6 = this.props.styles) === null || _this$props$styles6 === void 0 ? void 0 : _this$props$styles6.details, expandedStyles],
      children: expanded && this.renderContent()
    });
  }
  renderContent() {
    var _this$props$styles7;
    return _jsx("div", {
      css: (_this$props$styles7 = this.props.styles) === null || _this$props$styles7 === void 0 ? void 0 : _this$props$styles7.content,
      children: this.props.children
    });
  }
  render() {
    return _jsx(Expandable, {
      ...pickProps(this.props, Expandable.allowedProps),
      onToggle: this.handleToggle,
      children: ({
        expanded,
        getToggleProps,
        getDetailsProps
      }) => {
        var _this$props$styles8;
        return _jsxs("div", {
          css: (_this$props$styles8 = this.props.styles) === null || _this$props$styles8 === void 0 ? void 0 : _this$props$styles8.toggleDetails,
          ref: el => {
            this.ref = el;
          },
          children: [this.renderToggle(getToggleProps(), expanded), this.renderDetails(expanded, getDetailsProps())]
        });
      }
    });
  }
}, _ToggleDetails.displayName = "ToggleDetails", _ToggleDetails.componentId = 'ToggleDetails', _ToggleDetails.allowedProps = allowedProps, _ToggleDetails.propTypes = propTypes, _ToggleDetails.defaultProps = {
  variant: 'default',
  size: 'medium',
  fluidWidth: false,
  icon: IconArrowOpenEndSolid,
  iconExpanded: IconArrowOpenDownSolid,
  iconPosition: 'start',
  defaultExpanded: false,
  children: null
}, _ToggleDetails)) || _class) || _class);
export default ToggleDetails;
export { ToggleDetails };